import React, { useCallback, useEffect, useState } from "react";
import ApplicationQuestionsHandler from "./ApplicationQuestions/ApplicationQuestionsHandler";
import {
  fetchApplicationQuestions,
  ApplicationQuestions,
  QuestionType,
} from "src/network/fetchApplicationQuestions";
import "./ApplicationSection.css";
import { addNewApplication } from "src/network/addNewApplication";
import DropZone, { FileWithPreview } from "src/components/DropZone/DropZone";
import RandomSumPopup from "./RandomSumPopup";
import { useIsMobile } from "src/hooks/useIsMobile";

const excludedTextDependantQuestions = ["q36", "q39", "q43"];

function ApplicationSection() {
  const [applicationQuestions, setApplicationQuestions] =
    useState<ApplicationQuestions>();
  const [submissionLoading, setSubmissionLoading] = useState<boolean>(false);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [personalPic, setPersonalPic] = useState<File | null>(null);
  const [files, setFiles] = useState<FileWithPreview[]>([]);
  const [extraFiles, setExtraFiles] = useState<File[]>([]);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const maxDate = yesterday.toISOString().split("T")[0];
  const { isMobile } = useIsMobile();

  useEffect(() => {
    const getApplications = async () => {
      try {
        setSubmissionLoading(true);
        const questions = await fetchApplicationQuestions();
        setApplicationQuestions(questions);
        setSubmissionLoading(false);
      } catch (error) {
        setSubmissionLoading(false);
        console.error("Failed to fetch application questions:", error);
      }
    };

    getApplications();
  }, []);

  const allSections = applicationQuestions?.groupedQuestions
    ? Object.values(applicationQuestions.groupedQuestions)
    : [];

  const validateForm = () => {
    let isValid = true;
    const newErrors: Record<string, string> = {};

    if (!file) {
      newErrors.file = "ID/Passport upload is required.";
      isValid = false;
    }

    if (!answers.name?.trim()) {
      newErrors.name = "Name is required.";
      isValid = false;
    }

    if (!selectedDate) {
      newErrors.birthDate = "Birth date is required.";
      isValid = false;
    }

    if (!answers.address?.trim()) {
      newErrors.address = "Address is required.";
      isValid = false;
    }

    if (!answers.phone_number?.trim()) {
      newErrors.phone_number = "Phone number is required.";
      isValid = false;
    }

    if (!answers.email?.trim()) {
      newErrors.email = "Email is required.";
      isValid = false;
    }

    if (!answers.occupation?.trim()) {
      newErrors.occupation = "Occupation is required.";
      isValid = false;
    }

    if (!answers.appointment) {
      newErrors.appointment = "Appointment date is required.";
      isValid = false;
    }

    // Validate declaration question (q43)
    if (!answers.q43?.trim()) {
      newErrors.q43 = "Declaration signature is required.";
      isValid = false;
    }

    setErrors(newErrors);
    return { isValid, newErrors };
  };

  function handleAnswerChange(questionNumber: string, value: string) {
    setAnswers((prev) => ({ ...prev, [questionNumber]: value }));
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (selectedFile) {
      setFile(selectedFile);
      setAnswers((prev) => ({ ...prev, card_id_file_name: selectedFile.name }));
      setErrors((prev) => ({ ...prev, file: undefined }));
    }
  };

  const handlePersonalPicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files ? e.target.files[0] : null;
    if (selectedFile) {
      setPersonalPic(selectedFile);
      setAnswers((prev) => ({ ...prev, personal_pic_file_name: selectedFile.name }));
      setErrors((prev) => ({ ...prev, personal_pic: undefined }));
    }
  };

  const handleImageButtonClick = () => {
    const fileInput = document.getElementById(
      "file-upload"
    ) as HTMLInputElement;
    fileInput?.click();
  };

  const handlePersonalPicButtonClick = () => {
    const fileInput = document.getElementById(
      "personal-photo-upload"
    ) as HTMLInputElement;
    fileInput?.click();
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    setErrors((prev) => ({ ...prev, birthDate: undefined }));
  };

  const handleSubmitApplication = useCallback(async () => {
    try {
      setSubmissionLoading(true);

      const applicationToSubmit = {
        birth_date: selectedDate,
        ...answers,
        card_id_file_name: file,
        personal_pic_file_name: personalPic,
        extra_files: extraFiles,
        files,
      };

      const response = await addNewApplication(applicationToSubmit);
      setSubmissionLoading(false);

      if (response.success) {
        if (response.redirectUrl) {
          window.location.href = response.redirectUrl;
        } else {
          alert("Application submitted successfully");
        }
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      setSubmissionLoading(false);
      console.error("Error submitting application:", error);
      alert(`Failed to submit the application: ${error.message || error}`);
    }
  }, [file, answers, selectedDate, files, extraFiles, personalPic]);

  return (
    <div className="application-flex-container">
      <RandomSumPopup
        isVisible={isPopupVisible}
        onClose={() => setPopupVisible(false)}
        onSuccess={handleSubmitApplication}
      />
      {/* ID/Passport Upload */}
      <div className="image-picker-container">
        <label>
          id/ passport : صورة البطاقة الشخصية او الباسبور{" "}
          <span className="required-asterisk">*</span>
        </label>
        <button className="upload-button" onClick={handleImageButtonClick}>
          {file ? file.name : "Upload"}
        </button>
        <input
          id="file-upload"
          className="image-picker"
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        {errors.file && <div className="error-message">{errors.file}</div>}
      </div>

      {/* Personal Photo Upload */}
      <div className="image-picker-container">
        <label>
          Personal Photo : صورة شخصية
        </label>
        <button className="upload-button" onClick={handlePersonalPicButtonClick}>
          {personalPic ? personalPic.name : "Upload"}
        </button>
        <input
          id="personal-photo-upload"
          className="image-picker"
          type="file"
          accept="image/*"
          onChange={handlePersonalPicChange}
          style={{ display: "none" }}
        />
        {errors.personal_pic && <div className="error-message">{errors.personal_pic}</div>}
      </div>

      <div className="image-picker-container">
        <label>
          Extra Files : ملفات إضافية
        </label>
        <button
          className="upload-button"
          onClick={() => {
            const input = document.getElementById("extra-files-upload") as HTMLInputElement;
            input?.click();
          }}
        >
          {answers.extra_files_names
            ? answers.extra_files_names.split(',').length > 1
              ? `${answers.extra_files_names.split(',').length} files selected`
              : answers.extra_files_names
            : "Upload"}
        </button>
        <input
          id="extra-files-upload"
          className="image-picker"
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={e => {
            const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
            setExtraFiles(selectedFiles);
            setAnswers(prev => ({ ...prev, extra_files_names: selectedFiles.map(f => f.name).join(", ") }));
          }}
        />
        {errors.extra_files && <div className="error-message">{errors.extra_files}</div>}
      </div>
      
      <div style={{ display: "none" }}>
        <DropZone files={files} setFiles={setFiles} />
      </div>
      <div className="input-fields-container">
        <div className="input-field-wrapper">
          <input
            type="text"
            placeholder="Name"
            value={answers.name || ""}
            onChange={(e) => {
              setAnswers({ ...answers, name: e.target.value });
              setErrors((prev) => ({ ...prev, name: undefined }));
            }}
            required
          />
          <span className="required-asterisk">*</span>
          {errors.name && <div className="error-message">{errors.name}</div>}
        </div>
        <div style={styles.dateContainer}>
          <label style={styles.label}>
            birth date <span className="required-asterisk">*</span>
          </label>
          <input
            style={isMobile ? styles.mobileDate : styles.desktopDate}
            id="birth-date"
            type="date"
            title="Birth Date"
            value={selectedDate}
            onChange={handleDateChange}
            max={maxDate}
            required
          />
        </div>
        {errors.birthDate && (
          <div className="error-message">{errors.birthDate}</div>
        )}
        <div className="input-field-wrapper full-row">
          <input
            type="text"
            placeholder="Address"
            value={answers.address || ""}
            onChange={(e) =>
              setAnswers({ ...answers, address: e.target.value })
            }
            required
          />
          <span className="required-asterisk">*</span>
          {errors.address && (
            <div className="error-message">{errors.address}</div>
          )}
        </div>
        <div className="input-field-wrapper">
          <input
            type="text"
            placeholder="Phone Number"
            value={answers.phone_number || ""}
            onChange={(e) => {
              setAnswers({ ...answers, phone_number: e.target.value });
              setErrors((prev) => ({ ...prev, phone_number: undefined }));
            }}
            required
          />
          <span className="required-asterisk">*</span>
          {errors.phone_number && (
            <div className="error-message">{errors.phone_number}</div>
          )}
        </div>
        <div className="input-field-wrapper">
          <input
            type="email"
            placeholder="Email"
            value={answers.email || ""}
            onChange={(e) => setAnswers({ ...answers, email: e.target.value })}
            required
          />
          <span className="required-asterisk">*</span>
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>
        <div className="input-field-wrapper">
          <input
            type="text"
            placeholder="Company"
            value={answers.company || ""}
            onChange={(e) =>
              setAnswers({ ...answers, company: e.target.value })
            }
          />
        </div>
        <div className="input-field-wrapper">
          <input
            type="text"
            placeholder="Occupation"
            value={answers.occupation || ""}
            onChange={(e) =>
              setAnswers({ ...answers, occupation: e.target.value })
            }
            required
          />
          <span className="required-asterisk">*</span>
          {errors.occupation && (
            <div className="error-message">{errors.occupation}</div>
          )}
        </div>
        <div style={styles.dateContainer}>
          <label style={styles.label}>
            appointment <span className="required-asterisk">*</span>
          </label>
          <input
            style={isMobile ? styles.mobileDate : styles.desktopDate}
            type="date"
            placeholder="Appointment"
            title="Appointment"
            value={answers.appointment || ""}
            onChange={(e) =>
              setAnswers({ ...answers, appointment: e.target.value })
            }
          />
        </div>
        {errors.appointment && (
          <div className="error-message">{errors.appointment}</div>
        )}
        <div style={styles.dateContainer}>
          <label style={styles.label}>last medical assessment</label>
          <input
            style={isMobile ? styles.mobileDate : styles.desktopDate}
            type="date"
            placeholder="Last Medical Assessment"
            title="Last Medical Assessment"
            value={answers.last_medical_assessment || ""}
            onChange={(e) =>
              setAnswers({
                ...answers,
                last_medical_assessment: e.target.value,
              })
            }
          />
        </div>
      </div>

      {allSections.map((section) => (
        <>
          {section?.name?.en && (
            <div className="section-title">
              {section.name.en} - {section.name.ar || ""}
            </div>
          )}
          {section?.questions?.map((q, qIndex) => {
            let firstPreviousQuestionIndex = -1;
            for (let i = qIndex - 1; i >= 0; i--) {
              if (section.questions[i].question_type === QuestionType.YesNo) {
                firstPreviousQuestionIndex = i;
                break;
              }
            }

            const shouldShowText =
              firstPreviousQuestionIndex < 0 ||
              excludedTextDependantQuestions.includes(q.question_number) ||
              (q.question_type === QuestionType.Text &&
                answers[
                  section.questions[firstPreviousQuestionIndex].question_number
                ] === "1");
            return (
              <>
                <ApplicationQuestionsHandler
                  key={qIndex}
                  question={q}
                  index={qIndex}
                  shouldShowText={shouldShowText}
                  handleAnswerChange={handleAnswerChange}
                />
                {q.question_number === "q43" && errors.q43 && (
                  <div className="error-message" style={{ marginTop: "5px" }}>
                    {errors.q43}
                  </div>
                )}
              </>
            );
          })}
        </>
      ))}

      <button
        className="submit-application-button"
        onClick={() => {
          const { isValid, newErrors } = validateForm();
          if (isValid) {
            setPopupVisible(true);
          } else {
            // Only scroll if there are errors other than the declaration error
            const hasOtherErrors = Object.entries(newErrors).some(
              ([key, value]) => key !== "q43" && value !== undefined
            );
            if (hasOtherErrors) {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }
        }}
        disabled={submissionLoading}
      >
        {submissionLoading ? (
          <span className="loading-spinner"></span>
        ) : (
          "Submit Application"
        )}
      </button>
    </div>
  );
}

const styles = {
  row: {
    display: "flex",
    flexDirection: "row" as "row",
    alignItems: "center",
    gap: 20,
    justifyContent: "space-between",
  },
  label: {
    fontSize: "16px",
    fontWeight: 700,
    color: "#32AAFF",
  },
  dateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  mobileDate: {
    width: window.innerWidth,
    display: "flex",
    maxWidth: "50%",
  },
  desktopDate: {
    minWidth: "50%",
    display: "flex",
  },
};

export default ApplicationSection;
